import {
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { archiveProject, getAllProjects } from '../../../actions/projectActions'
import { PROJECT_ARCHIVE_RESET } from '../../../constants/projectConstants'
import { useInput } from '../../../hooks/useInput'
import FormContainer from '../../Form/FormContainer'
import TextAreaInput from '../../Form/TextAreaInput'
import ModalPopUp from '../../ModalPopUp/ModalPopUp'

enum Reasons {
  CLOSE_WON = 'Close won',
  IGNORE_CLOSE_LOST = 'Ignore close lost',
  NOT_NEEDED = 'Project no longer needed',
  DUPLICATE = 'Project was a duplicate',
  DECLINED = 'Customer declined work',
  OTHER = 'Other',
}

const CustomFormControl = styled(FormControl)`
  &.MuiFormControl-root {
    border: none;
    width: 100%;
  }
`

const Actions = styled.div`
  position: absolute;
  bottom: 20px;
  display: flex;
  justify-content: space-between;
  right: 30px;
  width: calc(100% - 60px);
  align-items: flex-end;
`

interface Props {
  editMode?: boolean
  setShowArchiveModal: (value: boolean) => void
  showArchiveModal: boolean
}

const ArchiveModal = ({
  editMode = true,
  setShowArchiveModal,
  showArchiveModal,
}: Props) => {
  const dispatch = useDispatch()

  const [reason, setReason] = useState('')
  const [edit, setEdit] = useState(editMode)

  const {
    bind: bindComments,
    error: commentsError,
    value: comments,
    setValue: setComments,
  } = useInput('')

  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)
  const { project } = useSelector(
    (state: RootStateOrAny) => state.projectDetails
  )
  const { success } = useSelector(
    (state: RootStateOrAny) => state.projectArchive
  )

  useEffect(() => {
    if (success) {
      dispatch({ type: PROJECT_ARCHIVE_RESET })
      setShowArchiveModal(false)
    }
  }, [setShowArchiveModal, success, dispatch])

  useEffect(() => {
    if (project && project.archiveReason) {
      if (
        project.archiveReason !== Reasons.NOT_NEEDED &&
        project.archiveReason !== Reasons.DUPLICATE &&
        project.archiveReason !== Reasons.DECLINED
      ) {
        setReason(Reasons.OTHER)
        setComments(project.archiveComments)
      } else {
        setReason(project.archiveReason)
      }
    }
  }, [project, setComments])

  useEffect(() => {
    if (success && userInfo.isAdmin) {
      dispatch(getAllProjects())
      setShowArchiveModal(false)
    }
  }, [dispatch, setShowArchiveModal, success, userInfo])

  const handleSubmit = () => {
    const projectClone = {
      ...project,
      status: 'Archived',
      archiveReason: reason,
      archiveComments: comments,
    }

    dispatch(archiveProject(projectClone))
  }

  const toggleEditMode = () => {
    setEdit(!edit)
  }

  return (
    <ModalPopUp
      title="Archive"
      setShowModal={setShowArchiveModal}
      showModal={showArchiveModal}
      variant="medium"
    >
      <FormContainer style={{ padding: 0, minHeight: 360 }}>
        <h4 className="m-b-30">{edit ? 'Archive' : 'Archived'} project</h4>
        <p>Reason:</p>
        {edit ? (
          <CustomFormControl>
            <RadioGroup
              name="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{
                '& .MuiRadio-root.Mui-checked': {
                  color: 'var(--color-primary)',
                },
              }}
            >
              <FormControlLabel
                value={Reasons.CLOSE_WON}
                control={<Radio />}
                label={Reasons.CLOSE_WON}
              />
              <FormControlLabel
                value={Reasons.IGNORE_CLOSE_LOST}
                control={<Radio />}
                label={Reasons.IGNORE_CLOSE_LOST}
              />
              <FormControlLabel
                value={Reasons.NOT_NEEDED}
                control={<Radio />}
                label={Reasons.NOT_NEEDED}
              />
              <FormControlLabel
                value={Reasons.DUPLICATE}
                control={<Radio />}
                label={Reasons.DUPLICATE}
              />
              <FormControlLabel
                value={Reasons.DECLINED}
                control={<Radio />}
                label={Reasons.DECLINED}
              />
              <FormControlLabel
                value={Reasons.OTHER}
                control={<Radio />}
                label={`${Reasons.OTHER} (include details)`}
              />
            </RadioGroup>
            <p className="m-t-16 m-b-6">Comments:</p>
            <TextAreaInput
              name="comments"
              bindValue={bindComments}
              error={commentsError}
              autoComplete="off"
              className="m-b-60"
              icon={null}
              disabled={false}
            />
          </CustomFormControl>
        ) : (
          <>
            <p className="m-b-40">{project.archiveReason}</p>

            <p>Comments: </p>

            <p>{project.archiveComments}</p>
          </>
        )}

        <Actions>
          {!edit ? (
            <span className="underline blue fs12" onClick={toggleEditMode}>
              Edit
            </span>
          ) : (
            <div></div>
          )}
          <div>
            <Button
              color="primary"
              variant="outlined"
              className="m-r-24"
              onClick={() => setShowArchiveModal(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={!reason}
              onClick={handleSubmit}
            >
              Archive
            </Button>
          </div>
        </Actions>
      </FormContainer>
    </ModalPopUp>
  )
}

export default ArchiveModal
